// Sylas Chains — a chain of iron links shoots from caster to target,
// briefly binding them, then snaps back.
// Phases: EXTEND → HOLD → SNAP

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const EXTEND_MS = 220;
const HOLD_MS   = 90;
const SNAP_MS   = 200;
const TOTAL     = EXTEND_MS + HOLD_MS + SNAP_MS;

const LINK_SPACING = 12; // pixels between link centers
const LINK_W       = 8;  // link long axis half-length
const LINK_H       = 4;  // link short axis half-length

export class SylasChainVFX implements Animation {
  private elapsed    = 0;
  private totalDist: number;
  private angle:     number;

  constructor(
    private from: Point,
    private to:   Point,
    private onImpact?: () => void,
    private onSnap?:   () => void,
  ) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    this.totalDist = Math.sqrt(dx * dx + dy * dy);
    this.angle     = Math.atan2(dy, dx);
  }

  update(dt: number): void {
    const prev = this.elapsed;
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (prev < EXTEND_MS && this.elapsed >= EXTEND_MS) this.onImpact?.();
    if (prev < EXTEND_MS + HOLD_MS && this.elapsed >= EXTEND_MS + HOLD_MS) this.onSnap?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const e = this.elapsed;
    if (e < EXTEND_MS) {
      const reach = Easing.easeOutQuad(e / EXTEND_MS) * this.totalDist;
      this.drawChain(ctx, 0, reach, 1);
    } else if (e < EXTEND_MS + HOLD_MS) {
      this.drawChain(ctx, 0, this.totalDist, 1);
    } else {
      const snapT  = Easing.easeInQuad((e - EXTEND_MS - HOLD_MS) / SNAP_MS);
      const tail   = snapT * this.totalDist;          // tail retracts toward caster
      const alpha  = 1 - snapT * 0.6;
      this.drawChain(ctx, tail, this.totalDist, alpha);
    }
  }

  private drawChain(
    ctx: CanvasRenderingContext2D,
    startDist: number,
    endDist:   number,
    alpha:     number,
  ): void {
    if (endDist <= 0) return;

    const cos = Math.cos(this.angle);
    const sin = Math.sin(this.angle);
    const originX = this.from.x;
    const originY = this.from.y;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.shadowColor = "#33cccc";
    ctx.shadowBlur  = 10;

    // Draw the chain rope (background line)
    const sx = originX + cos * startDist;
    const sy = originY + sin * startDist;
    const ex = originX + cos * endDist;
    const ey = originY + sin * endDist;
    ctx.strokeStyle = "#226666";
    ctx.lineWidth   = 3;
    ctx.shadowBlur  = 0;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(ex, ey);
    ctx.stroke();

    // Draw individual chain links
    const firstLink = Math.ceil(startDist / LINK_SPACING);
    const lastLink  = Math.floor(endDist  / LINK_SPACING);

    for (let i = firstLink; i <= lastLink; i++) {
      const d = i * LINK_SPACING;
      if (d < startDist || d > endDist) continue;

      const lx  = originX + cos * d;
      const ly  = originY + sin * d;
      // Alternate horizontal and vertical links
      const linkAngle = this.angle + (i % 2 === 0 ? 0 : Math.PI / 2);

      ctx.save();
      ctx.translate(lx, ly);
      ctx.rotate(linkAngle);

      // Link outer ring
      ctx.strokeStyle = "#448888";
      ctx.lineWidth   = LINK_H * 2;
      ctx.shadowColor = "#33aaaa";
      ctx.shadowBlur  = 8;
      ctx.lineCap     = "round";
      ctx.beginPath();
      ctx.moveTo(-LINK_W, 0);
      ctx.lineTo(LINK_W, 0);
      ctx.stroke();

      // Link inner highlight
      ctx.strokeStyle = "#88dddd";
      ctx.lineWidth   = 1.5;
      ctx.shadowBlur  = 4;
      ctx.beginPath();
      ctx.moveTo(-LINK_W + 2, 0);
      ctx.lineTo(LINK_W - 2, 0);
      ctx.stroke();

      ctx.restore();
    }

    // Glowing tip at the leading edge
    const tipX = originX + cos * endDist;
    const tipY = originY + sin * endDist;
    ctx.globalAlpha = alpha;
    ctx.shadowColor = "#44ffff";
    ctx.shadowBlur  = 16;
    ctx.fillStyle   = "#88ffff";
    ctx.beginPath();
    ctx.arc(tipX, tipY, 4.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
